import React from "react";
import { Check } from "lucide-react";
import { Trans } from "@lingui/react/macro";

export interface SlotOption {
  id: string;
  label: string;
}

interface SlotTimePickerProps {
  slots: SlotOption[];
  selectedIds: Set<string>;
  disabledIds?: Set<string>;
  onToggle: (id: string) => void;
}

interface TimeGroup {
  key: "morning" | "afternoon" | "evening";
  minTime: string;
  maxTime: string;
}

const TIME_GROUPS: TimeGroup[] = [
  { key: "morning", minTime: "00:00", maxTime: "12:00" },
  { key: "afternoon", minTime: "12:00", maxTime: "17:00" },
  { key: "evening", minTime: "17:00", maxTime: "24:00" },
];

function GroupLabel({ groupKey }: { groupKey: TimeGroup["key"] }) {
  switch (groupKey) {
    case "morning":
      return <Trans>Morning</Trans>;
    case "afternoon":
      return <Trans>Afternoon</Trans>;
    case "evening":
      return <Trans>Evening</Trans>;
  }
}

const SlotTimePicker: React.FC<SlotTimePickerProps> = ({
  slots,
  selectedIds,
  disabledIds = new Set(),
  onToggle,
}) => {
  const groups = TIME_GROUPS.map((g) => ({
    key: g.key,
    slots: slots.filter((s) => s.label >= g.minTime && s.label < g.maxTime),
  })).filter((g) => g.slots.length > 0);

  if (groups.length === 0) return null;

  return (
    <div className="space-y-5">
      {groups.map((group) => (
        <div key={group.key}>
          <div className="mb-2.5 flex items-center gap-2">
            <span className="text-xs font-semibold tracking-wide text-gray-400 uppercase">
              <GroupLabel groupKey={group.key} />
            </span>
            <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-500">
              {group.slots.length}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {group.slots.map((slot) => {
              const isSelected = selectedIds.has(slot.id);
              const isDisabled = disabledIds.has(slot.id);
              return (
                <button
                  key={slot.id}
                  disabled={isDisabled}
                  onClick={() => onToggle(slot.id)}
                  className={`flex h-10 min-w-[4.5rem] items-center justify-center gap-1.5 rounded-lg border px-3 text-sm font-medium transition-colors ${
                    isDisabled
                      ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-300"
                      : isSelected
                        ? "border-soluud-primary bg-soluud-primary text-white shadow-sm"
                        : "text-soluud-text-primary hover:border-soluud-primary hover:bg-soluud-primary-subtle border-gray-200"
                  }`}
                >
                  {isSelected && <Check className="h-3.5 w-3.5 shrink-0" />}
                  <span>{slot.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SlotTimePicker;
